import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';

export interface ABTestSession {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  base_prompt: string;
  test_type: 'model_comparison' | 'parameter_testing' | 'style_variation' | 'custom';
  status: 'active' | 'completed' | 'archived';
  winner_variant_id: string | null;
  total_votes: number;
  metadata: any;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  share_token?: string;
  is_public?: boolean;
}

export const useABTestingSessions = () => {
  const { user } = useSecureAuthContext();
  const [sessions, setSessions] = useState<ABTestSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSessions = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('ab_test_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setSessions((data || []) as ABTestSession[]);
      setError(null);
    } catch (err) {
      console.error('Error fetching A/B test sessions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  };

  const createSession = async (sessionData: Omit<ABTestSession, 'id' | 'user_id' | 'total_votes' | 'created_at' | 'updated_at'>) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { data, error: createError } = await supabase
        .from('ab_test_sessions')
        .insert({
          user_id: user.id,
          ...sessionData
        })
        .select()
        .single();

      if (createError) throw createError;
      
      setSessions(prev => [data as ABTestSession, ...prev]);
      return { data, error: null };
    } catch (err) {
      console.error('Error creating A/B test session:', err);
      return { data: null, error: err instanceof Error ? err.message : 'Failed to create session' };
    }
  };

  const updateSession = async (sessionId: string, updates: Partial<ABTestSession>) => {
    try {
      const { data, error: updateError } = await supabase
        .from('ab_test_sessions')
        .update(updates)
        .eq('id', sessionId)
        .select()
        .single();

      if (updateError) throw updateError;

      setSessions(prev => prev.map(s => s.id === sessionId ? (data as ABTestSession) : s));
      return { data, error: null };
    } catch (err) {
      console.error('Error updating A/B test session:', err);
      return { data: null, error: err instanceof Error ? err.message : 'Failed to update session' };
    }
  };

  const deleteSession = async (sessionId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('ab_test_sessions')
        .delete()
        .eq('id', sessionId);

      if (deleteError) throw deleteError;

      setSessions(prev => prev.filter(s => s.id !== sessionId));
      return { error: null };
    } catch (err) {
      console.error('Error deleting A/B test session:', err);
      return { error: err instanceof Error ? err.message : 'Failed to delete session' };
    }
  };

  const togglePublicAccess = async (sessionId: string, isPublic: boolean) => {
    try {
      const { error } = await supabase
        .from('ab_test_sessions')
        .update({ is_public: isPublic })
        .eq('id', sessionId);

      if (error) throw error;

      await fetchSessions();
      return { data: true, error: null };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to toggle public access';
      return { data: null, error: errorMsg };
    }
  };

  useEffect(() => {
    fetchSessions();

    if (!user) return;

    // Real-time subscription with unique timestamp to prevent conflicts
    const channel = supabase
      .channel(`ab-sessions-${user.id}-${Date.now()}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'ab_test_sessions',
        filter: `user_id=eq.${user.id}`
      }, () => {
        fetchSessions();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return {
    sessions,
    loading,
    error,
    createSession,
    updateSession,
    deleteSession,
    togglePublicAccess,
    refetch: fetchSessions
  };
};
